import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ArrowBack, TimerOff, LocationOn, MonetizationOn, Visibility, Close } from "@mui/icons-material";
import api from "../../api/axios";
import SortableHeader from "../../components/SortableHeader";
import { useSortable } from "../../hooks/useSortable";
import { useSearchFilter } from "../../hooks/useSearchFilter";

function formatDate(timestamp) {
  if (!timestamp) return "—";
  const d = new Date(timestamp);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/* ── Helpers ── */
function formatCurrency(value) {
  if (typeof value === "number") return `₹${value.toLocaleString("en-IN")}`;
  if (value === 0) return "₹0";
  return value || "—";
}

const CARD_ACCENTS = {
  totalAmount: { icon: "#dc2626", bg: "rgba(220,38,38,0.10)", border: "#fee2e2" },
  totalCommission: { icon: "#16a34a", bg: "rgba(22,163,74,0.10)", border: "#dcfce7" },
};

const LIMIT = 20;

export default function AgentOrdersLost() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState(null);

  const { sortKey, sortDir, handleSort, applySort } = useSortable();
  const { searchTerm, setSearchTerm, applyFilter } = useSearchFilter();

  const fetchPage = useCallback(async (pageNum) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const params = new URLSearchParams({ page: pageNum, limit: LIMIT });
      const { data } = await api.get(`/agent/notifications/expired?${params}`);
      const newOrders = data.notifications || [];

      setAllOrders((prev) => pageNum === 1 ? newOrders : [...prev, ...newOrders]);
      setPage(pageNum);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error("Failed to fetch lost orders:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const handleLoadMore = useCallback(() => {
    fetchPage(page + 1);
  }, [page, fetchPage]);

  const hasMore = allOrders.length < totalCount;

  const sortConfig = useMemo(() => ({
    orderId: { getValue: (o) => (o.orderId || "").toLowerCase() },
    sentAt: { getValue: (o) => new Date(o.sentAt || 0).getTime() },
    vendorName: { getValue: (o) => (o.vendorName || "").toLowerCase() },
    totalAmount: { getValue: (o) => Number(o.totalAmount) || 0 },
    commission: { getValue: (o) => Number(o.commission) || 0 },
    distanceKm: { getValue: (o) => Number(o.distanceKm) || 0 },
  }), []);

  const searchConfig = useMemo(() => [
    { getValue: (o) => o.orderId },
    { getValue: (o) => o.vendorName },
    { getValue: (o) => o.vendorCity },
    { getValue: (o) => o.totalAmount },
    { getValue: (o) => o.commission },
    { getValue: (o) => o.distanceKm },
    { getValue: (o) => o.sentAt && new Date(o.sentAt).toLocaleDateString("en-IN") },
  ], []);

  const filteredOrders = useMemo(() => applyFilter(allOrders, searchConfig), [allOrders, searchConfig, applyFilter]);
  const sortedOrders = useMemo(() => applySort(filteredOrders, sortConfig), [filteredOrders, sortConfig, applySort]);

  const totals = useMemo(() => {
    let totalAmount = 0, totalCommission = 0;
    allOrders.forEach(o => {
      if (o.totalAmount) totalAmount += Number(o.totalAmount);
      if (o.commission) totalCommission += Number(o.commission);
    });
    return { totalAmount, totalCommission };
  }, [allOrders]);

  const kpiCards = useMemo(() => [
    { id: "totalAmount", title: "Total Order Value", value: formatCurrency(totals.totalAmount) },
    { id: "totalCommission", title: "Total Commission Lost", value: formatCurrency(totals.totalCommission) },
  ], [totals]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #0062ff 100%)", px: { xs: 2, sm: 4 }, py: 2.5 }}>
        <Button onClick={() => navigate("/agent/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 1, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
          <ArrowBack sx={{ fontSize: 16 }} />
          Back to Dashboard
        </Button>
        <Typography variant="h5" fontWeight={700} sx={{ color: "#fff", mb: 0.5 }}>⏳ Orders Lost</Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>Orders that expired before you could accept them</Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 4 }, py: 3, maxWidth: 1200, mx: "auto" }}>
        {/* KPI Widgets */}
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(2, 1fr)" }, alignItems: "stretch", mb: 3 }}>
          {kpiCards.map((c) => {
            const accent = CARD_ACCENTS[c.id] || CARD_ACCENTS.totalAmount;
            return (
              <Box key={c.id} sx={{ p: 1.25, borderRadius: "12px", bgcolor: "#fff", border: `1.5px solid ${accent.border}`, boxShadow: "0 2px 10px rgba(15,23,42,0.06)", display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.25 }, width: "100%", boxSizing: "border-box", minHeight: { xs: 80, sm: 80, md: 90 } }}>
                <Box sx={{ width: { xs: 34, sm: 38, lg: 36 }, height: { xs: 34, sm: 38, lg: 36 }, borderRadius: "10px", bgcolor: accent.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <MonetizationOn sx={{ fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem", lg: "0.68rem" }, fontWeight: 500, color: "#64748b", lineHeight: 1.2, mb: 0.4, overflow: "hidden", textOverflow: "ellipsis" }}>{c.title}</Typography>
                  {loading ? (
                    <Box sx={{ width: "70%", height: 24, bgcolor: "#e2e8f0", borderRadius: 1 }} />
                  ) : (
                    <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.2rem", lg: "1rem" }, fontWeight: 700, lineHeight: 1, color: "#0f172a" }}>{c.value}</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Details Table */}
        <Paper sx={{ borderRadius: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1e293b", display: "flex", alignItems: "center", gap: 1 }}>
              <TimerOff sx={{ fontSize: 18, color: "#dc2626" }} />
              Lost Order Details ({allOrders.length} of {totalCount} orders loaded)
            </Typography>
          </Box>

          <TextField
            size="small"
            placeholder="🔍 Filter orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mx: 2, mt: 1.5, mb: 0, "& input": { fontSize: "0.82rem" } }}
          />

          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={32} sx={{ color: "#dc2626", mb: 1.5 }} /><Typography variant="body2" sx={{ color: "#94a3b8" }}>Loading lost orders...</Typography></Box>
          ) : sortedOrders.length === 0 ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "rgba(220,38,38,0.08)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}><TimerOff sx={{ fontSize: 32, color: "#dc2626" }} /></Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1e293b", mb: 0.5 }}>No lost orders</Typography>
              <Typography variant="body2" sx={{ color: "#94a3b8" }}>Great job! You haven't missed any orders recently.</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <SortableHeader label="Order" column="orderId" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortableHeader label="Order Date" column="sentAt" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortableHeader label="Vendor" column="vendorName" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortableHeader label="Amount" column="totalAmount" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortableHeader label="Commission" column="commission" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortableHeader label="Distance" column="distanceKm" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedOrders.map((order, idx) => (
                      <TableRow key={`${order.orderId}-${idx}`} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} sx={{ color: "#1e293b", fontSize: "0.74rem", whiteSpace: "nowrap" }}>
                            #{order.orderId ? order.orderId.substring(0, 8).toUpperCase() : "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell><Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", whiteSpace: "nowrap" }}>{formatDate(order.sentAt)}</Typography></TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b", fontSize: "0.78rem" }}>{order.vendorName || "—"}</Typography>
                          {order.vendorCity && <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem", display: "block" }}>{order.vendorCity}</Typography>}
                        </TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b", fontSize: "0.78rem" }}>{formatCurrency(order.totalAmount)}</Typography></TableCell>
                        <TableCell><Typography variant="body2" sx={{ color: "#16a34a", fontSize: "0.78rem" }}>{formatCurrency(order.commission)}</Typography></TableCell>
                        <TableCell>
                          {order.distanceKm ? (
                            <Chip icon={<LocationOn sx={{ fontSize: 12 }} />} label={`${order.distanceKm.toFixed(1)} km`} size="small" sx={{ fontWeight: 500, fontSize: "0.65rem", height: 22, bgcolor: "rgba(100,116,139,0.10)", color: "#64748b" }} />
                          ) : (
                            <Typography variant="caption" sx={{ color: "#94a3b8" }}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => setSelected(order)} title="View order details">
                            <Visibility sx={{ fontSize: 20, color: "#64748b" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {hasMore && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, px: 4 }}>
                    {loadingMore ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>

      {/* LOST ORDER DETAILS (read-only snapshot - never exposes other agent or live status) */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b" }}>Lost Order Details</Typography>
          <IconButton onClick={() => setSelected(null)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Order #</Typography>
                <Typography variant="body1" fontWeight={700} sx={{ color: "#1e293b" }}>#{selected.orderId ? selected.orderId.substring(0, 8).toUpperCase() : "N/A"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Vendor</Typography>
                <Typography variant="body1" sx={{ color: "#334155" }}>{selected.vendorName || "N/A"}</Typography>
                {selected.vendorCity && <Typography variant="caption" sx={{ color: "#94a3b8" }}>{selected.vendorCity}</Typography>}
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Amount</Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ color: "#1e293b" }}>{formatCurrency(selected.totalAmount)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Commission</Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ color: "#16a34a" }}>{formatCurrency(selected.commission)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Expired On</Typography>
                  <Typography variant="body1" sx={{ color: "#334155" }}>{formatDate(selected.sentAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Distance</Typography>
                  <Typography variant="body1" sx={{ color: "#334155" }}>{selected.distanceKm ? `${selected.distanceKm.toFixed(1)} km` : "N/A"}</Typography>
                </Box>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block", mb: 1 }}>Items ({selected.items?.length || 0})</Typography>
                {selected.items?.length ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {selected.items.map((it, i) => (
                      <Box key={i} sx={{ display: "flex", justifyContent: "space-between", bgcolor: "#f8fafc", borderRadius: 1, px: 1.25, py: 0.6 }}>
                        <Typography variant="caption" sx={{ color: "#475569", fontWeight: 500 }}>{it.title || "Item"}{it.qty ? ` x${it.qty}` : ""}</Typography>
                        {it.price != null && <Typography variant="caption" fontWeight={600} sx={{ color: "#1e293b" }}>{formatCurrency(it.price)}</Typography>}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ color: "#94a3b8" }}>No items</Typography>
                )}
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, display: "block" }}>Delivery Address</Typography>
                <Typography variant="body2" sx={{ color: "#334155" }}>
                  {selected.address ? [selected.address.addressLine1, selected.address.addressLine2].filter(Boolean).join(", ") || "N/A" : "N/A"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                  {selected.address ? [selected.address.city, selected.address.state, selected.address.pincode].filter(Boolean).join(", ") : ""}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} variant="outlined" sx={{ textTransform: "none", fontWeight: 600 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}